// utils/artifactUtils.js - 아티팩트 관련 유틸리티 함수

import { marked } from "marked";

/**
 * 아티팩트 URL 생성
 * @param {string} studySlug - 연구 slug
 * @param {string|number} artifactId - 아티팩트 ID
 * @returns {string} 아티팩트 URL
 */
export const getArtifactUrl = (studySlug, artifactId) => {
  return `/study/${studySlug}/artifact/${artifactId}`;
};

/**
 * Study URL 생성
 * @param {string} studySlug - 연구 slug
 * @returns {string} Study URL
 */
export const getStudyUrl = (studySlug) => {
  return `/study/${studySlug}`;
};

/**
 * 아티팩트 타입별 한글명 변환
 * @param {string} type - 아티팩트 타입 ('plan', 'survey', 'guideline')
 * @returns {string} 한글명
 */
export const getArtifactTypeName = (type) => {
  const typeMap = {
    'plan': '리서치 계획서',
    'survey': '스크리닝 설문',
    'guideline': '진행 가이드라인'
  };
  return typeMap[type] || '아티팩트';
};

export const getArtifactDeletedToastMessage = (type) => {
  const messageMap = {
    plan: '리서치 계획서를 삭제했습니다.',
    survey: '스크리닝 설문을 삭제했습니다.',
    guideline: '진행 가이드라인을 삭제했습니다.',
  };
  return messageMap[type] || '산출물을 삭제했습니다.';
};

/**
 * 아티팩트 타입별 제목 변환
 * @param {string} type - 아티팩트 타입
 * @returns {string} 제목
 */
export const getArtifactTitle = (type) => {
  switch (type) {
    case "plan":
      return "리서치 계획서";
    case "screener":
      return "참여자 선별기";
    case "survey":
      return "스크리닝 설문";
    case "guideline":
      return "진행 가이드라인";
    default:
      return "";
  }
};

/**
 * 인증 헤더 생성
 * @param {object} options - 옵션
 * @param {boolean} options.includeContentType - Content-Type 헤더 포함 여부 (기본값: false)
 * @returns {object} 헤더 객체
 */
export const getAuthHeaders = (options = {}) => {
  const { includeContentType = false } = options;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = {};
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  // JWT는 HttpOnly Cookie로 자동 전송됨 (Authorization 헤더 불필요)
  if (user.id) {
    headers['X-User-ID'] = user.id.toString();
  }
  
  return headers;
};

/**
 * Survey 데이터 파싱
 * @param {string} content - 아티팩트 콘텐츠
 * @returns {object|null} 파싱된 Survey 데이터 또는 null
 */
export const parseSurveyData = (content) => {
  if (!content) return null;
  
  const match = content.match(/<!-- SURVEY_DATA\n([\s\S]*?)\n-->/);
  if (match) {
    try {
      return JSON.parse(match[1]);
    } catch (e) {
      console.error("JSON 파싱 실패:", e);
      return null;
    }
  }
  return null;
};

const DEFAULT_SURVEY_BLOCKS = [
  { id: 'intro', title: 'Block 0: 안내/동의', kind: 'intro' },
  { id: 'A_qualification', title: 'Block A: 필수 자격 (Qualification)', kind: 'qualification' },
  { id: 'B_demographics', title: 'Block B: 배경 정보 (Demographics)', kind: 'demographics' },
  { id: 'C_open_ended', title: 'Block C: 심층 질문 (Open-ended)', kind: 'open_ended' },
  { id: 'D_ops', title: 'Block D: 운영 정보 (Ops)', kind: 'ops' },
];

const mapLegacyQuestionElement = (type = '') => {
  const normalized = String(type);
  if (normalized.includes('단일 선택') || normalized.includes('객관식')) return 'RadioButtons';
  if (normalized.includes('다중 선택') || normalized.includes('다중선택')) return 'Checkboxes';
  if (normalized.includes('서술형')) return 'TextArea';
  return 'TextInput';
};

const normalizeSurveyOptions = (options) => {
  if (!Array.isArray(options)) return [];
  return options
    .map((opt) => {
      if (typeof opt === 'string') return opt.trim();
      return String(opt?.text || opt?.value || '').trim();
    })
    .filter(Boolean);
};

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const getSurveyFormElements = (surveyData) => {
  if (!surveyData) return [];

  if (Array.isArray(surveyData.form_elements) && surveyData.form_elements.length > 0) {
    return surveyData.form_elements;
  }

  if (!Array.isArray(surveyData.questions) || surveyData.questions.length === 0) {
    return [];
  }

  return surveyData.questions.map((question, index) => ({
    id: question.id || `q${index + 1}`,
    text: question.text || '',
    element: mapLegacyQuestionElement(question.type),
    required: question.required !== false,
    block_id: question.block_id || question.section_id,
    options: question.options,
  }));
};

const groupSurveyElementsByBlock = (surveyData, elements) => {
  const blocks = Array.isArray(surveyData?.blocks) && surveyData.blocks.length > 0
    ? surveyData.blocks
    : DEFAULT_SURVEY_BLOCKS;

  const groups = blocks.map((block) => ({ block, items: [] }));
  const groupById = new Map(groups.map((group) => [group.block?.id, group]));
  const fallbackGroup =
    groupById.get((blocks[blocks.length - 1] || {}).id)
    || groupById.get('D_ops')
    || groups[groups.length - 1];

  elements.forEach((element) => {
    const blockId = element?.block_id || element?.section_id;
    const group = (blockId && groupById.get(blockId)) || fallbackGroup;
    if (group) group.items.push(element);
  });

  return groups.filter((group) => group.items.length > 0);
};

/**
 * 스크리닝 설문을 구글 독스 등에 붙여넣기 좋은 형식으로 변환
 * @param {object} surveyData - parseSurveyData 결과
 * @returns {{ plain: string, html: string }}
 */
export const formatSurveyClipboardContent = (surveyData) => {
  const elements = getSurveyFormElements(surveyData);
  if (!elements.length) {
    return { plain: '', html: '' };
  }

  const grouped = groupSurveyElementsByBlock(surveyData, elements);
  const plainParts = [];
  const htmlParts = [];
  let questionNumber = 0;

  grouped.forEach(({ block, items }) => {
    const blockTitle = (block?.title || block?.id || '설문 문항').trim();
    plainParts.push(blockTitle, '');
    htmlParts.push(`<h2 style="font-size:13pt;font-weight:700;margin:18px 0 8px;">${escapeHtml(blockTitle)}</h2>`);

    items.forEach((item) => {
      questionNumber += 1;
      const questionText = String(item?.text || '').trim();
      const options = normalizeSurveyOptions(item?.options);

      plainParts.push(`${questionNumber}. ${questionText}`);
      htmlParts.push(
        `<p style="margin:10px 0 4px;line-height:1.6;"><strong>${questionNumber}.</strong> ${escapeHtml(questionText)}</p>`,
      );

      if (options.length > 0) {
        options.forEach((optionText) => {
          plainParts.push(`   • ${optionText}`);
        });
        htmlParts.push(
          `<ul style="margin:0 0 12px 20px;padding:0;line-height:1.6;">${
            options.map((optionText) => `<li>${escapeHtml(optionText)}</li>`).join('')
          }</ul>`,
        );
      }

      plainParts.push('');
    });

    plainParts.push('');
  });

  return {
    plain: plainParts.join('\n').trim(),
    html: `<div style="font-family:'Noto Sans KR',Arial,sans-serif;font-size:11pt;color:#111;">${htmlParts.join('')}</div>`,
  };
};

/**
 * 마크다운을 HTML로 변환
 * @param {string} markdown - 마크다운 문자열
 * @returns {string} HTML 문자열
 */
export const markdownToHtml = (markdown) => {
  if (!markdown) return "";

  try {
    // marked를 사용하여 마크다운을 HTML로 변환
    const html = marked.parse(markdown);
    return html;
  } catch (error) {
    console.error("마크다운 변환 실패:", error);
    return markdown; // 실패 시 원본 반환
  }
};

